import { faCopy } from '@fortawesome/pro-regular-svg-icons';

import { Discount } from '../model/Discounts.model';
import {
  ListRow,
  SchemaCell,
  toGridListSchema,
} from '../view/components/GridList';
import {
  boldText,
  col,
  typography,
  linkButton,
  ListCell,
  menu,
  menuItem,
  pill,
  row,
  smallText,
  text,
  title,
  tooltip,
} from '../view/components/ListCell';
import { DiscountsVM, Filters } from './discounts.viewModel';
import { formatDateWithTime } from './utils';
import { SubscriptionType } from '../../collection/domain';

export function capitalizeFirstLetter(str: string): string {
  if (!str || !str.length) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

type Mapper<T = ListCell> = (
  v: Discount,
  dispatch: DiscountsVM['dispatch'],
  isCreatorLive: boolean,
) => T;

// The grid cell breaks words, which lets a flex-shrunk title collapse
// mid-word next to the badge. Only the badged row needs holding open.
const toNameCell = (v: Discount) =>
  v.isReferralCampaign
    ? row(
        [
          typography({
            content: v.name,
            variant: 'body',
            size: 'medium',
            fontWeight: 'medium',
            style: { whiteSpace: 'nowrap' },
          }),
          pill({
            content: 'User referral',
            type: 'Primary',
            maxWidth: 'max-content',
          }),
        ],
        { gap: 'm', alignCenter: true },
      )
    : title(v.name);

const toExpireCell: Mapper = (v) =>
  col([
    toNameCell(v),
    ...(v.expiresAt !== null
      ? [smallText('Expires: ' + formatDateWithTime(v.expiresAt))]
      : []),
  ]);

const toDateCell: Mapper = (v) =>
  col([title(new Date(v.createdAt).toLocaleDateString())]);

const toCodeCell: Mapper = (v, dispatch) =>
  tooltip(
    'Your discount code you can share with your audience.',
    pill({
      type: 'Neutral',
      content: v.code,
      ...(v.status === 'Active'
        ? {
            onClick: () => dispatch('CopyCode', v.code),
            icon: faCopy,
          }
        : {}),
      maxWidth: 110,
    }),
  );

const toStatusCell: Mapper = (v) =>
  pill({
    content: v.status,
    type: v.status,
  });

const toOTPProduct: Mapper = (v) => {
  const p = v.product;
  if (p.type !== 'otp' || !p.value.name) return boldText('N/A');

  return v.status !== 'Expired'
    ? row([
        tooltip(
          'Open checkout page with your discount applied. Use this link to share with your audience!',
          linkButton({
            props: {
              to: v.url || '',
            },
            data: [boldText(capitalizeFirstLetter(p.value.name || 'N/A'))],
          }),
        ),
      ])
    : boldText(capitalizeFirstLetter(p.value.name || 'N/A'));
};

export const toPeriodLabel = (periods: SubscriptionType[]): string =>
  periods.length ? periods.map(capitalizeFirstLetter).join(' and ') : 'N/A';

const toSubscriptionProduct: Mapper = (v) => {
  const p = v.product;
  if (p.type !== 'subscription') return boldText('N/A');

  return col([
    smallText('Subscription'),
    row(
      v.status === 'Active'
        ? [
            tooltip(
              'Open checkout page with your discount applied. Use this link to share with your audience!',
              linkButton({
                props: {
                  to: v.url || '',
                },
                data: [boldText(toPeriodLabel(p.value.periods))],
              }),
            ),
          ]
        : [boldText(toPeriodLabel(p.value.periods))],
      { gap: 's' },
    ),
  ]);
};

const toProductCell: Mapper = (v, d, isCreatorLive) =>
  v.product.type === 'otp'
    ? toOTPProduct(v, d, isCreatorLive)
    : toSubscriptionProduct(v, d, isCreatorLive);

// Free trial applies to subscriptions only
export const toFreeTrialState = (v: Discount): 'ON' | 'OFF' | null =>
  v.product.type !== 'subscription'
    ? null
    : v.compatibleWithFreeTrial
    ? 'ON'
    : 'OFF';

const toFreeTrialCell: Mapper = (v) => text(toFreeTrialState(v) ?? '-');

const toDiscountValueCell: Mapper = (v) =>
  text(
    (v.amount.type === 'fixed'
      ? '$' + v.amount.value / 100
      : v.amount.value + '%') + ' off',
  );

const toRedemptionCell: Mapper = (v) =>
  row(
    [
      text(v.redemptions + ''),
      ...(v.redemptionsLimit != null ? [text('/' + v.redemptionsLimit)] : []),
    ],
    { gap: '0' },
  );

const toMenuCell: Mapper = (v, dispatch, isCreatorLive) =>
  menu([
    menuItem('See details', () => dispatch('OpenDetails', v.detailURL)),
    ...(v.isProductPublished && isCreatorLive
      ? [
          menuItem('Copy link', () =>
            dispatch('OpenConfirmModal', v.url || v.code),
          ),
        ]
      : []),
    menuItem(
      'Deactivate',
      () => dispatch('StartDeactivateDiscount', v.discountId),
      { color: 'error' },
    ),
  ]);

type Cell = { name: string; mapper: Mapper; width: SchemaCell['width'] };

const cell = ([name, mapper, width]: [
  string,
  Mapper,
  SchemaCell['width'],
]): Cell => ({
  name,
  mapper,
  width,
});

const cells = [
  ['Name', toExpireCell, '2fr'],
  ['Date Created', toDateCell, 100],
  ['Code', toCodeCell, 180],
  ['Status', toStatusCell, 120],
  ['Product', toProductCell, 180],
  ['Free trial', toFreeTrialCell, 100],
  ['Discount Value', toDiscountValueCell, 100],
  ['Redemptions', toRedemptionCell, 100],
  ['', toMenuCell, 50],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
].map((v) => cell(v as any));

export const discountRowSchema = toGridListSchema(
  cells.map((c) => [c.name, c.width]),
);

const toDiscountCells: Mapper<ListCell[]> = (v, dispatch, isCreatorLive) =>
  cells.map((c) => c.mapper(v, dispatch, isCreatorLive));

export const toDiscountRow: Mapper<ListRow> = (v, dispatch, isCreatorLive) => ({
  id: v.discountId + '',
  cells: toDiscountCells(v, dispatch, isCreatorLive),
  onClick: () => dispatch('OpenDetails', v.detailURL),
});

const filterByName = (search: string) => (discounts: Discount[]) => {
  const s = (search || '').toLocaleLowerCase();
  if (!s) return discounts;
  const m = search.match(/"([^"]*)"/g);
  if (!m)
    return discounts.filter(
      (d) =>
        d.name.toLowerCase().includes(s) ||
        d.code.toLowerCase().includes(s) ||
        (d.product.type === 'otp' &&
          d.product.value.name.toLocaleLowerCase().includes(s)) ||
        (d.product.type === 'subscription' &&
          d.product.value.periods.some((period) =>
            period.toLowerCase().includes(s),
          )) ||
        d.status.toLowerCase().includes(s),
    );

  const i = m[0].replace(/"/g, '').toLocaleLowerCase();
  return discounts.filter((d) => {
    const c = d.code.toLocaleLowerCase();
    const p = d.product;
    const name = d.name.toLocaleLowerCase();
    return (
      (p.type === 'otp' && p.value.name.toLowerCase() === i) ||
      c === i ||
      name === i
    );
  });
};

const filterByStatus = (status: Filters['status']) => (discounts: Discount[]) =>
  status !== 'All' ? discounts.filter((d) => d.status === status) : discounts;

const filterByType = (type: Filters['type']) => (discounts: Discount[]) =>
  type !== 'All' ? discounts.filter((d) => d.product.type === type) : discounts;

export const filterDiscounts = (
  discounts: Discount[],
  search: string,
  filters: Filters = { status: 'All', type: 'All' },
) =>
  [
    filterByName(search),
    filterByStatus(filters.status),
    filterByType(filters.type),
  ].reduce((acc, f) => f(acc), discounts);

export type OtpOptionType = 'OFFER' | 'PROGRAM';

export type Option<T = string> = {
  value: T;
  label: string;
  type?: OtpOptionType;
};

export const toOption = <T extends string | number>(
  value: T,
  label: string,
  type?: OtpOptionType,
): Option<T> => ({
  value,
  label,
  type,
});
